import styled from "@emotion/styled"
import { PointsOfInterestDto } from "api"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"
import { trackChooseLocationFromList } from "reusable/gtm"
import { NewText, useIsDesktop } from "ui"
import { getLogoByName } from "../Locator/logo"

const StyledContainer = styled.div`
  height: 100%;
  /* overflow-y: auto; */
  overflow: hidden;
  overflow-x: hidden;
  box-shadow:rgba(130, 136, 148, 0.2) 0px 0px 10px 0px;
  max-width: 100%;
  display: flex;
  flex-direction: column;


`

const StyledItem = styled.div<{ $isSelected: boolean }>`
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #153e694d;
  gap: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #ECF0F3;
  }

  &:last-child {
    border-bottom: none;
  }

    ${({ theme }) => theme.breakpoints.up("md")} {
        padding: 20px 30px;
        gap: 20px;
        background-color: ${props => props.$isSelected ? '#ECF0F3' : 'transparent'};
    }
  ${({ theme }) => theme.breakpoints.down("sm")} {
   background-color: #fff;
  }

    &:focus-visible {
        background-color: #ECF0F3;
        outline: 3px solid #153e69;
        outline-offset: -3px;
    }
`

const StyledItemLogo = styled.div`
  border-radius: 8px;
  position: relative;
  width: 60px;
  height: 60px;
  min-width: 60px;
  flex-shrink: 0;

  ${({ theme }) => theme.breakpoints.up("lg")} {
    width: 70px;
    height: 70px;
    min-width: 70px;
  }
`

const StyledItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 4px;
  overflow-wrap: break-word;
  word-break: break-word;
`

const StyledEmptyMessage = styled.div`
  padding: 24px 20px;
  text-align: center;

  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 40px 30px;
  }
`
const StyledResultsLabel = styled.div`
  background-color: #ECF0F3;
  padding: 20px 30px ;
  border-bottom: 1px solid #ABBBCA;
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
  line-height:1;
`

const StyledCloseButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  padding-bottom:8px;
  margin: 0;
  width:auto;
  height:auto;
  color: #000;
  font-size: 22px;
  font-weight: 100;
  line-height: 12px;
  transition: color 0.2s ease;

  &:hover {
    color: #000;
  }

  &:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
  }
`

const StyledTablist = styled.div`
  overflow: auto;


  /* ${({ theme }) => theme.breakpoints.down("sm")} {
    padding-bottom: 85px;
  } */
`
interface PazCenterListProps {
    items: PointsOfInterestDto[]
    selectedId?: string | null
    onItemClick?: (item: PointsOfInterestDto, index: number) => void
    onClose?: () => void
    showResultsLabel?: boolean
    className?: string
    expandedSelected?: boolean
    infoWindowOpen?: boolean
    gtmLabel?: string
}

function PazCenterList({ items, selectedId, onItemClick, onClose, showResultsLabel = true, className, expandedSelected, infoWindowOpen, gtmLabel }: PazCenterListProps) {
    const t = useTranslations('pazCenter')
    const isDesktop = useIsDesktop()
    const tabsRef = useRef<(HTMLDivElement | null)[]>([])
    const [focusedIndex, setFocusedIndex] = useState<number>(() => {
        if (!selectedId) {
            return 0
        }
        const idx = items.findIndex(it => (it.Id || '') === selectedId || String(it.Id) === selectedId)
        return idx >= 0 ? idx : 0
    })

    useEffect(() => {
        if (!selectedId) {
            return
        }
        const idx = items.findIndex(it => (it.Id || '') === selectedId || String(it.Id) === selectedId)
        if (idx >= 0) {
            setFocusedIndex(idx)
        }
    }, [selectedId, items])

    const handleItemClick = useCallback((item: PointsOfInterestDto, index: number) => {
        if (onItemClick) {
            onItemClick(item, index)
            const address = [item.Street, item.CityName].filter(Boolean).join(', ') || t('results.noAddress')
            trackChooseLocationFromList(item.ShortName, address, gtmLabel)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onItemClick, gtmLabel])

    const handleClose = () => {
        if (onClose) {
            onClose()
        }
    }

    const navigationActivationRef = useRef(false)

    const moveFocus = useCallback((nextIndex: number, activate?: boolean) => {
        const size = items.length
        if (size === 0) {
            return
        }
        const wrapped = (nextIndex + size) % size
        setFocusedIndex(wrapped)
        const el = tabsRef.current[wrapped]
        requestAnimationFrame(() => {
            el?.focus()
        })
        if (activate) {
            const item = items[wrapped]
            if (item && isDesktop) {
                navigationActivationRef.current = true
                handleItemClick(item, wrapped)
            }
        }
    }, [items, handleItemClick])

    useEffect(() => {
        if (navigationActivationRef.current) {
            const refocus = () => {
                const el = tabsRef.current[focusedIndex]
                el?.focus()
            }
            // Multiple attempts to override map/infoWindow auto focus
            const t1 = setTimeout(refocus, 30)
            const t2 = setTimeout(refocus, 120)
            const t3 = setTimeout(() => {
                refocus()
                navigationActivationRef.current = false
            }, 250)
            return () => {
                clearTimeout(t1)
                clearTimeout(t2)
                clearTimeout(t3)
            }
        }
    }, [focusedIndex, selectedId])

    const onKeyDownList = (e: React.KeyboardEvent) => {
        if (items.length === 0) {
            return
        }
        switch (e.key) {
            case 'ArrowDown':
            case 'ArrowLeft':
                e.preventDefault()
                moveFocus(focusedIndex + 1, true)
                break
            case 'ArrowUp':
            case 'ArrowRight':
                e.preventDefault()
                moveFocus(focusedIndex - 1, true)
                break
            case 'Home':
                e.preventDefault(); moveFocus(0, true); break
            case 'End':
                e.preventDefault(); moveFocus(items.length - 1, true); break
            case 'Enter':
            case ' ': {
                e.preventDefault()
                const item = items[focusedIndex]
                if (item) {
                    handleItemClick(item, focusedIndex)
                }
                break
            }
            case 'Tab': {
                if (!e.shiftKey && infoWindowOpen) {
                    const anchor = document.getElementById('pazcenter-infowindow-focus') as HTMLElement | null
                    if (anchor) {
                        e.preventDefault()
                        anchor.focus()
                    }
                }
                break
            }
        }
    }

    const total = items.length
    return (
        <StyledContainer className={className}>
            {showResultsLabel && (
                <StyledResultsLabel>
                    <StyledCloseButton
                        onClick={handleClose}
                        aria-label={t('results.close')}
                        title={t('results.close')}
                    >
                        ×
                    </StyledCloseButton>
                    <span>
                        {t('results.foundPrefix')}{' '}
                        <b style={{ fontSize: '28px' }}>{total}</b>{' '}
                        {t('results.foundSuffix')}
                    </span>
                </StyledResultsLabel>
            )}
            {total > 0 ? (
                <StyledTablist
                    role="tablist"
                    aria-label={t('results.tablistLabel')}
                    onKeyDown={onKeyDownList}
                >
                    {items.map((item: PointsOfInterestDto, index: number) => {
                        const markerId = item.Id || `marker-${index}`
                        const isSelected = selectedId === markerId || selectedId === String(item.Id)
                        const address = [item.Street, item.CityName].filter(Boolean).join(', ') || t('results.noAddress')
                        const selectedSuffix = item.PointTypes[0].TypeId === 9 ? t('results.plannedSuffix') : ''
                        return (
                            <StyledItem
                                key={item.Id}
                                ref={el => { tabsRef.current[index] = el }}
                                role="tab"
                                id={`pazcenter-tab-${markerId}`}
                                aria-controls={`pazcenter-panel-${markerId}`}
                                tabIndex={index === focusedIndex ? 0 : -1}
                                aria-selected={isSelected}
                                aria-expanded={isSelected ? !!expandedSelected : undefined}
                                aria-posinset={index + 1}
                                aria-setsize={total}
                                $isSelected={isSelected}
                                onClick={() => handleItemClick(item, index)}
                            >
                                <StyledItemLogo>
                                    <Image
                                        src={getLogoByName(selectedSuffix || item.ShortName, [])}
                                        fill
                                        alt=""
                                        style={{ objectFit: 'contain' }}
                                    />
                                </StyledItemLogo>
                                <StyledItemContent>
                                    <NewText
                                        component="h3"
                                        desktopFontSize={16}
                                        desktopLineHeight="22px"
                                        desktopFontWeight="600"
                                        largeDesktopFontSize={20}
                                        largeDesktopLineHeight="28px"
                                        mobileFontSize={18}
                                        mobileLineHeight="24px"
                                        color="#000"
                                    >
                                        {item.ShortName || item.Name}  {selectedSuffix}
                                    </NewText>
                                    <NewText
                                        component="p"
                                        desktopFontSize={14}
                                        desktopLineHeight="20px"
                                        desktopFontWeight="400"
                                        largeDesktopFontSize={16}
                                        largeDesktopLineHeight="22px"
                                        mobileFontSize={14}
                                        mobileLineHeight="20px"
                                        color="#666"
                                    >
                                        {address}
                                    </NewText>
                                </StyledItemContent>
                            </StyledItem>
                        )
                    })}
                </StyledTablist>
            ) : (
                <StyledEmptyMessage>
                    <NewText
                        component="p"
                        desktopFontSize={20}
                        desktopFontWeight={400}
                        desktopLineHeight="28px"
                        mobileFontSize={18}
                        mobileLineHeight="24px"
                        color="#666"
                    >
                        {t('results.none')}
                    </NewText>
                </StyledEmptyMessage>
            )}
        </StyledContainer>
    )
}

export default PazCenterList
